/**
 * UTM parameter utilities for the outreach → welcome → get-started flow.
 *
 * The outreach Lambda lands leads on /welcome with UTM params that carry
 * org type (utm_content) and lead ID (utm_term). These utilities:
 * 1. Map the UTM org-type slug to the intake form's OrgType enum
 * 2. Read UTM params from the current URL
 * 3. Build a /get-started URL that forwards all UTM params
 */

import type { OrgType } from '@/lib/public-trpc'

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

/**
 * Maps outreach UTM slugs (from url-rewriter.ts buildShortLink) to the
 * intake form's coarser OrgType enum. Multiple CRM org types collapse
 * into single form values (e.g. all ensemble types → 'ensemble').
 */
const UTM_SLUG_TO_FORM_ORG_TYPE: Record<string, OrgType> = {
  'church-music-program': 'church',
  'community-choir': 'ensemble',
  'community-orchestra': 'ensemble',
  'youth-orchestra': 'ensemble',
  'chamber-ensemble': 'ensemble',
  'jazz-ensemble': 'ensemble',
  'university-music-dept': 'school',
  'music-school': 'school',
  'piano-studio': 'teacher',
  'music-festival': 'festival',
  'opera-company': 'festival',
  'wedding-musician': 'musician',
}

/** Convert a UTM content slug to the intake form's OrgType, or undefined if unknown. */
export function utmSlugToFormOrgType(slug: string): OrgType | undefined {
  return UTM_SLUG_TO_FORM_ORG_TYPE[slug]
}

/** Read all UTM params from the current page URL. */
export function getUtmParams(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const result: Record<string, string> = {}
  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value) result[key] = value
  }
  return result
}

/** Get the utm_content slug from the current URL, or undefined. */
export function getUtmContentSlug(): string | undefined {
  if (typeof window === 'undefined') return undefined
  return new URLSearchParams(window.location.search).get('utm_content') ?? undefined
}

/**
 * Build a /get-started URL that forwards all current UTM params.
 * Used by the welcome page's secondary CTA so attribution survives
 * the navigation from /welcome → /get-started.
 */
export function buildGetStartedUrl(): string {
  const utmParams = getUtmParams()
  const qs = new URLSearchParams(utmParams).toString()
  return qs ? `/get-started?${qs}` : '/get-started'
}
